import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import {
  Users,
  GitMerge,
  History,
  AlertTriangle,
  ArrowRightLeft,
  CheckCircle2,
  ChevronRight,
  Loader2,
  Search,
  ShieldAlert,
  Building,
  Phone,
  Mail,
  MapPin,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api, formatApiError } from "@/lib/api";
import { formatCurrency, formatDateTime } from "@/lib/format";
import EmptyState from "@/components/EmptyState";

export default function CustomerMergingPanel() {
  const [activeSubTab, setActiveSubTab] = useState("candidates");

  // Duplicate candidates state
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidatesData, setCandidatesData] = useState({ total_groups: 0, groups: [] });

  // Merge history state
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyData, setHistoryData] = useState([]);

  // Merge modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sourceId, setSourceId] = useState(null);
  const [targetId, setTargetId] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  // Merge form state
  const [mergeReason, setMergeReason] = useState("");
  const [fieldOverrides, setFieldOverrides] = useState({});
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Manual search state
  const [allCustomers, setAllCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const loadCandidates = async () => {
    setCandidatesLoading(true);
    try {
      const res = await api.get("/customers/duplicate-candidates");
      setCandidatesData(res.data);
    } catch (err) {
      toast.error("Mükerrer adaylar yüklenemedi: " + formatApiError(err));
    } finally {
      setCandidatesLoading(false);
    }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get("/customers/merge-history?limit=100");
      setHistoryData(res.data);
    } catch (err) {
      toast.error("Birleştirme geçmişi yüklenemedi: " + formatApiError(err));
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadAllCustomers = async () => {
    try {
      const res = await api.get("/customers?page_size=200&is_active=true");
      setAllCustomers(res.data.items || []);
    } catch (err) {
      console.error("Customers list error:", err);
    }
  };

  useEffect(() => {
    loadCandidates();
    loadHistory();
    loadAllCustomers();
  }, []);

  const openMergeModal = (srcId, tgtId) => {
    setSourceId(srcId);
    setTargetId(tgtId);
    setFieldOverrides({});
    setMergeReason("");
    setIsConfirmed(false);
    setIsModalOpen(true);
  };

  // Fetch merge preview whenever source or target changes in modal
  useEffect(() => {
    if (!isModalOpen || !sourceId || !targetId || sourceId === targetId) {
      setPreviewData(null);
      setPreviewError(null);
      return;
    }

    let isMounted = true;
    const fetchPreview = async () => {
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const res = await api.post("/customers/merge-preview", {
          source_customer_id: sourceId,
          target_customer_id: targetId,
        });
        if (isMounted) {
          setPreviewData(res.data);
          // Initialize overrides with "target" for all conflict fields
          const initialOverrides = {};
          (res.data.conflict_fields || []).forEach((c) => {
            initialOverrides[c.field] = "target";
          });
          setFieldOverrides(initialOverrides);
        }
      } catch (err) {
        if (isMounted) {
          setPreviewError(formatApiError(err));
          setPreviewData(null);
        }
      } finally {
        if (isMounted) setPreviewLoading(false);
      }
    };

    fetchPreview();
    return () => {
      isMounted = false;
    };
  }, [isModalOpen, sourceId, targetId]);

  const handleSwapRoles = () => {
    const temp = sourceId;
    setSourceId(targetId);
    setTargetId(temp);
  };

  const handleExecuteMerge = async () => {
    if (!isConfirmed) {
      toast.warning("Lütfen işlemi onaylayınız.");
      return;
    }
    if (!previewData || !previewData.can_merge) {
      toast.error("Birleştirme koşulları sağlanmadı.");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        source_customer_id: sourceId,
        target_customer_id: targetId,
        reason: mergeReason.trim() || undefined,
        field_overrides: fieldOverrides,
      };

      const res = await api.post("/customers/merge", payload);
      toast.success(res.data.message || "Müşteri birleştirme işlemi başarıyla tamamlandı.");
      setIsModalOpen(false);
      loadCandidates();
      loadHistory();
      loadAllCustomers();
    } catch (err) {
      toast.error("Birleştirme başarısız: " + formatApiError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredCustomers = useMemo(() => {
    if (!searchQuery.trim()) return allCustomers;
    const q = searchQuery.toLowerCase();
    return allCustomers.filter(
      (c) =>
        c.display_name?.toLowerCase().includes(q) ||
        c.customer_number?.toLowerCase().includes(q) ||
        c.phone?.includes(q) ||
        c.company_name?.toLowerCase().includes(q)
    );
  }, [allCustomers, searchQuery]);

  return (
    <div className="space-y-6" data-testid="customer-merging-panel">
      {/* Top Banner */}
      <Card className="bg-white border-[#DCE2E8] shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="text-xl font-bold text-[#171A1F] flex items-center gap-2">
                <GitMerge className="w-5 h-5 text-indigo-600" />
                Müşteri Tekilleştirme ve Kontrollü Birleştirme
              </CardTitle>
              <CardDescription className="text-xs text-slate-500 mt-1">
                Tarihsel mükerrer kayıtları güvenle tek bir ana müşteride toplayın. Tüm işler, siparişler,
                tahsilatlar ve makbuzlar eksiksiz korunur.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  loadCandidates();
                  loadHistory();
                }}
                disabled={candidatesLoading || historyLoading}
                className="text-xs"
              >
                Yenile
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Sub Tabs */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="space-y-4">
        <TabsList className="bg-white border border-[#DCE2E8] shadow-sm p-1">
          <TabsTrigger value="candidates" className="text-xs sm:text-sm flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-600" />
            Mükerrer Adaylar
            {candidatesData.total_groups > 0 && (
              <Badge variant="secondary" className="bg-amber-100 text-amber-800 text-[11px] px-1.5 py-0.2">
                {candidatesData.total_groups} Grup
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="manual" className="text-xs sm:text-sm flex items-center gap-2">
            <Search className="w-4 h-4 text-slate-600" />
            Manuel Müşteri Seçimi
          </TabsTrigger>
          <TabsTrigger value="history" className="text-xs sm:text-sm flex items-center gap-2">
            <History className="w-4 h-4 text-slate-600" />
            Birleştirme Geçmişi
          </TabsTrigger>
        </TabsList>

        {/* 1. Mükerrer Adaylar Tab */}
        <TabsContent value="candidates" className="space-y-4">
          {candidatesLoading ? (
            <div className="flex items-center justify-center p-12 bg-white rounded-lg border border-[#DCE2E8]">
              <Loader2 className="w-6 h-6 animate-spin text-slate-500 mr-2" />
              <span className="text-sm text-slate-600">Mükerrer kayıtlar taranıyor...</span>
            </div>
          ) : candidatesData.groups.length === 0 ? (
            <EmptyState
              title="Mükerrer Kayıt Bulunamadı"
              description="Sistemdeki tüm aktif müşteriler telefon, e-posta ve isim bazında tekil durumda."
            />
          ) : (
            <div className="space-y-4">
              {candidatesData.groups.map((group, gIdx) => (
                <Card key={gIdx} className="bg-white border-[#DCE2E8] shadow-sm overflow-hidden">
                  <div className="bg-slate-50 px-4 py-3 border-b border-[#DCE2E8] flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className={
                          group.confidence === "high"
                            ? "bg-amber-50 text-amber-700 border-amber-300"
                            : "bg-blue-50 text-blue-700 border-blue-300"
                        }
                      >
                        {group.match_type === "phone" && "Telefon Eşleşmesi"}
                        {group.match_type === "email" && "E-posta Eşleşmesi"}
                        {group.match_type === "name" && "İsim Benzerliği"}
                      </Badge>
                      <span className="text-xs font-semibold text-slate-800">{group.match_value}</span>
                      <span className="text-xs text-slate-500">({group.customers.length} kayıt)</span>
                    </div>
                    {group.customers.length >= 2 && (
                      <Button
                        size="sm"
                        onClick={() => openMergeModal(group.customers[1].id, group.customers[0].id)}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-8"
                      >
                        <GitMerge className="w-3.5 h-3.5 mr-1" />
                        Bu Kayıtları İncele ve Birleştir
                      </Button>
                    )}
                  </div>
                  <Table>
                    <TableHeader className="bg-white">
                      <TableRow className="border-b border-slate-100 text-xs">
                        <TableHead className="w-28">Müşteri No</TableHead>
                        <TableHead>Müşteri Adı / Firma</TableHead>
                        <TableHead>İletişim</TableHead>
                        <TableHead className="text-right">İş Sayısı</TableHead>
                        <TableHead className="text-right">Toplam Satış</TableHead>
                        <TableHead className="text-right">Açık Alacak</TableHead>
                        <TableHead className="w-24 text-center">İşlem</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {group.customers.map((c) => (
                        <TableRow key={c.id} className="text-xs hover:bg-slate-50/50">
                          <TableCell className="font-mono text-slate-600">{c.customer_number}</TableCell>
                          <TableCell>
                            <div className="font-medium text-slate-900">{c.display_name}</div>
                            {c.company_name && <div className="text-[11px] text-slate-500">{c.company_name}</div>}
                          </TableCell>
                          <TableCell>
                            <div className="text-slate-700">{c.phone || "—"}</div>
                            <div className="text-[11px] text-slate-500">{c.email || "—"}</div>
                          </TableCell>
                          <TableCell className="text-right font-medium text-slate-800">{c.job_count}</TableCell>
                          <TableCell className="text-right text-slate-700">{formatCurrency(c.total_sales)}</TableCell>
                          <TableCell className="text-right font-medium text-amber-700">
                            {formatCurrency(c.outstanding_receivables)}
                          </TableCell>
                          <TableCell className="text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-xs h-7 text-indigo-600 hover:text-indigo-800"
                              onClick={() => {
                                const other = group.customers.find((otherC) => otherC.id !== c.id);
                                if (other) openMergeModal(c.id, other.id);
                              }}
                            >
                              Hedefe Aktar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* 2. Manuel Seçim Tab */}
        <TabsContent value="manual" className="space-y-4">
          <Card className="bg-white border-[#DCE2E8] shadow-sm p-4">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Search className="w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Müşteri adı, no veya telefon ile ara..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="max-w-md text-xs"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Source Selection */}
                <div className="border border-red-200 bg-red-50/30 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-bold text-red-900 uppercase">
                      1. Kaynak Müşteri (Aktarılacak / Kapatılacak)
                    </Label>
                    {sourceId && (
                      <Button variant="ghost" size="sm" onClick={() => setSourceId(null)} className="h-6 text-[11px] text-red-700">
                        Seçimi Kaldır
                      </Button>
                    )}
                  </div>
                  {sourceId ? (
                    (() => {
                      const c = allCustomers.find((x) => x.id === sourceId);
                      return (
                        <div className="bg-white p-3 rounded border border-red-200 text-xs space-y-1">
                          <div className="font-bold text-slate-900">{c?.display_name}</div>
                          <div className="text-slate-500">{c?.customer_number} • {c?.phone}</div>
                        </div>
                      );
                    })()
                  ) : (
                    <div className="text-xs text-slate-500 italic">Aşağıdaki listeden kaynak müşteriyi seçin.</div>
                  )}
                </div>

                {/* Target Selection */}
                <div className="border border-green-200 bg-green-50/30 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-bold text-green-900 uppercase">
                      2. Hedef Müşteri (Kalıcı / Ana Müşteri)
                    </Label>
                    {targetId && (
                      <Button variant="ghost" size="sm" onClick={() => setTargetId(null)} className="h-6 text-[11px] text-green-700">
                        Seçimi Kaldır
                      </Button>
                    )}
                  </div>
                  {targetId ? (
                    (() => {
                      const c = allCustomers.find((x) => x.id === targetId);
                      return (
                        <div className="bg-white p-3 rounded border border-green-200 text-xs space-y-1">
                          <div className="font-bold text-slate-900">{c?.display_name}</div>
                          <div className="text-slate-500">{c?.customer_number} • {c?.phone}</div>
                        </div>
                      );
                    })()
                  ) : (
                    <div className="text-xs text-slate-500 italic">Aşağıdaki listeden hedef müşteriyi seçin.</div>
                  )}
                </div>
              </div>

              {sourceId && targetId && sourceId !== targetId && (
                <div className="flex justify-end pt-2">
                  <Button
                    onClick={() => setIsModalOpen(true)}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs"
                  >
                    <GitMerge className="w-3.5 h-3.5 mr-1" />
                    Seçilen İki Müşteriyi İncele ve Birleştir
                  </Button>
                </div>
              )}

              {/* Customers selection list */}
              <div className="border border-[#DCE2E8] rounded-md max-h-96 overflow-y-auto">
                <Table>
                  <TableHeader className="bg-slate-50 sticky top-0">
                    <TableRow className="text-xs">
                      <TableHead>Müşteri No</TableHead>
                      <TableHead>Müşteri Adı</TableHead>
                      <TableHead>Telefon</TableHead>
                      <TableHead>İlçe/İl</TableHead>
                      <TableHead className="text-right">Seçim</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCustomers.slice(0, 50).map((c) => (
                      <TableRow key={c.id} className="text-xs hover:bg-slate-50/50">
                        <TableCell className="font-mono text-slate-600">{c.customer_number}</TableCell>
                        <TableCell className="font-medium text-slate-900">{c.display_name}</TableCell>
                        <TableCell className="text-slate-600">{c.phone || "—"}</TableCell>
                        <TableCell className="text-slate-600">
                          {[c.district, c.city].filter(Boolean).join(" / ") || "—"}
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button
                            variant={sourceId === c.id ? "default" : "outline"}
                            size="sm"
                            className="h-7 text-[11px] px-2"
                            onClick={() => setSourceId(c.id)}
                            disabled={targetId === c.id}
                          >
                            Kaynak Yap
                          </Button>
                          <Button
                            variant={targetId === c.id ? "default" : "outline"}
                            size="sm"
                            className="h-7 text-[11px] px-2"
                            onClick={() => setTargetId(c.id)}
                            disabled={sourceId === c.id}
                          >
                            Hedef Yap
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* 3. Birleştirme Geçmişi Tab */}
        <TabsContent value="history" className="space-y-4">
          {historyLoading ? (
            <div className="flex items-center justify-center p-12 bg-white rounded-lg border border-[#DCE2E8]">
              <Loader2 className="w-6 h-6 animate-spin text-slate-500 mr-2" />
              <span className="text-sm text-slate-600">Geçmiş yükleniyor...</span>
            </div>
          ) : historyData.length === 0 ? (
            <EmptyState
              title="Henüz Birleştirme Yapılmadı"
              description="Gerçekleştirilen tüm müşteri birleştirme işlemleri denetim amacıyla burada listelenir."
            />
          ) : (
            <Card className="bg-white border-[#DCE2E8] shadow-sm overflow-hidden">
              <Table>
                <TableHeader className="bg-slate-50">
                  <TableRow className="text-xs">
                    <TableHead>Tarih</TableHead>
                    <TableHead>İşlemi Yapan</TableHead>
                    <TableHead>Kapatılan Kaynak Müşteri</TableHead>
                    <TableHead>Aktarılan Hedef Müşteri</TableHead>
                    <TableHead className="text-center">Aktarılan İş / Ödeme</TableHead>
                    <TableHead>Gerekçe</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {historyData.map((h) => (
                    <TableRow key={h.id} className="text-xs hover:bg-slate-50/50">
                      <TableCell className="text-slate-600">{formatDateTime(h.merged_at)}</TableCell>
                      <TableCell className="font-medium text-slate-900">{h.merged_by_name || "Sistem"}</TableCell>
                      <TableCell>
                        <span className="line-through text-red-700 mr-1 font-medium">
                          {h.source_customer_name || "Bilinmiyor"}
                        </span>
                        <span className="text-[10px] text-slate-400">
                          ({h.metadata_snapshot?.source_customer?.customer_number || ""})
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-green-800 font-medium">{h.target_customer_name || "Bilinmiyor"}</span>
                        <span className="text-[10px] text-slate-400 ml-1">
                          ({h.metadata_snapshot?.target_customer?.customer_number || ""})
                        </span>
                      </TableCell>
                      <TableCell className="text-center font-mono">
                        {h.affected_job_ids?.length || 0} İş / {h.affected_payment_ids?.length || 0} Ödeme
                      </TableCell>
                      <TableCell className="text-slate-600 italic">{h.reason || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Controlled Merge Review & Confirmation Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="customer-merge-modal">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              <GitMerge className="w-5 h-5 text-indigo-600" />
              Kontrollü Müşteri Birleştirme İncelemesi
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Kaynak müşteri pasif duruma getirilecek ve tüm işleri hedef müşteriye devredilecektir.
            </DialogDescription>
          </DialogHeader>

          {previewLoading ? (
            <div className="flex flex-col items-center justify-center p-12 space-y-3">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              <p className="text-xs text-slate-600">Birleştirme önizlemesi ve finansal kontroller yapılıyor...</p>
            </div>
          ) : previewError ? (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md text-xs text-red-800 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
              <div>
                <strong>Birleştirme Yapılamaz:</strong> {previewError}
              </div>
            </div>
          ) : previewData ? (
            <div className="space-y-6 text-xs">
              {/* Warnings Banner */}
              {previewData.warnings?.length > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-amber-800 space-y-1">
                  <div className="font-bold flex items-center gap-1.5 text-xs text-amber-900">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    Önemli Bilgilendirme:
                  </div>
                  {previewData.warnings.map((w, idx) => (
                    <div key={idx} className="text-[11px] ml-5 list-disc">
                      • {w}
                    </div>
                  ))}
                </div>
              )}

              {/* Side-by-Side Customer Comparison */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 relative">
                {/* Swap button in center */}
                <div className="hidden md:flex absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                  <Button
                    size="icon"
                    variant="outline"
                    className="rounded-full shadow-md bg-white border-slate-300 hover:bg-slate-100"
                    onClick={handleSwapRoles}
                    title="Rolleri Değiştir (Hedef ↔ Kaynak)"
                  >
                    <ArrowRightLeft className="w-4 h-4 text-slate-700" />
                  </Button>
                </div>

                {/* Source Card */}
                <Card className="border-red-200 bg-red-50/20 shadow-none">
                  <CardHeader className="py-2.5 px-3 bg-red-100/50 border-b border-red-200">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-red-900 uppercase text-[11px]">
                        Kaynak Müşteri (Pasife Alınacak)
                      </span>
                      <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-300">
                        {previewData.source_customer.customer_number}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="p-3 space-y-2">
                    <div className="text-sm font-bold text-slate-900">{previewData.source_customer.display_name}</div>
                    {previewData.source_customer.company_name && (
                      <div className="text-slate-600 flex items-center gap-1">
                        <Building className="w-3 h-3 text-slate-400" />
                        {previewData.source_customer.company_name}
                      </div>
                    )}
                    <div className="text-slate-600 flex items-center gap-1">
                      <Phone className="w-3 h-3 text-slate-400" />
                      {previewData.source_customer.phone || "—"}
                    </div>
                    <div className="text-slate-600 flex items-center gap-1">
                      <Mail className="w-3 h-3 text-slate-400" />
                      {previewData.source_customer.email || "—"}
                    </div>
                    <div className="text-slate-600 flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-slate-400" />
                      {[previewData.source_customer.district, previewData.source_customer.city].filter(Boolean).join(" / ") || "—"}
                    </div>

                    <div className="pt-2 border-t border-red-100 grid grid-cols-2 gap-2 text-[11px]">
                      <div>İş Sayısı: <strong className="text-slate-800">{previewData.source_customer.job_count}</strong></div>
                      <div>Satış: <strong className="text-slate-800">{formatCurrency(previewData.source_customer.total_sales)}</strong></div>
                      <div>Tahsilat: <strong className="text-slate-800">{formatCurrency(previewData.source_customer.total_collections)}</strong></div>
                      <div>Alacak: <strong className="text-red-700">{formatCurrency(previewData.source_customer.outstanding_receivables)}</strong></div>
                    </div>
                  </CardContent>
                </Card>

                {/* Target Card */}
                <Card className="border-green-200 bg-green-50/20 shadow-none">
                  <CardHeader className="py-2.5 px-3 bg-green-100/50 border-b border-green-200">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-green-900 uppercase text-[11px]">
                        Hedef Müşteri (Kalıcı / Ana Kayıt)
                      </span>
                      <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-300">
                        {previewData.target_customer.customer_number}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="p-3 space-y-2">
                    <div className="text-sm font-bold text-slate-900">{previewData.target_customer.display_name}</div>
                    {previewData.target_customer.company_name && (
                      <div className="text-slate-600 flex items-center gap-1">
                        <Building className="w-3 h-3 text-slate-400" />
                        {previewData.target_customer.company_name}
                      </div>
                    )}
                    <div className="text-slate-600 flex items-center gap-1">
                      <Phone className="w-3 h-3 text-slate-400" />
                      {previewData.target_customer.phone || "—"}
                    </div>
                    <div className="text-slate-600 flex items-center gap-1">
                      <Mail className="w-3 h-3 text-slate-400" />
                      {previewData.target_customer.email || "—"}
                    </div>
                    <div className="text-slate-600 flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-slate-400" />
                      {[previewData.target_customer.district, previewData.target_customer.city].filter(Boolean).join(" / ") || "—"}
                    </div>

                    <div className="pt-2 border-t border-green-100 grid grid-cols-2 gap-2 text-[11px]">
                      <div>İş Sayısı: <strong className="text-slate-800">{previewData.target_customer.job_count}</strong></div>
                      <div>Satış: <strong className="text-slate-800">{formatCurrency(previewData.target_customer.total_sales)}</strong></div>
                      <div>Tahsilat: <strong className="text-slate-800">{formatCurrency(previewData.target_customer.total_collections)}</strong></div>
                      <div>Alacak: <strong className="text-green-800">{formatCurrency(previewData.target_customer.outstanding_receivables)}</strong></div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Combined Financial Impact Summary */}
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                <div className="font-semibold text-slate-800 mb-2">Birleştirme Sonrası Finansal Tablo</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                  <div className="bg-white p-2 rounded border border-slate-200">
                    <div className="text-[10px] text-slate-500 uppercase">Toplam Satış</div>
                    <div className="text-sm font-bold text-slate-900">
                      {formatCurrency(previewData.financial_impact.combined_sales)}
                    </div>
                  </div>
                  <div className="bg-white p-2 rounded border border-slate-200">
                    <div className="text-[10px] text-slate-500 uppercase">Toplam Tahsilat</div>
                    <div className="text-sm font-bold text-green-700">
                      {formatCurrency(previewData.financial_impact.combined_collections)}
                    </div>
                  </div>
                  <div className="bg-white p-2 rounded border border-slate-200">
                    <div className="text-[10px] text-slate-500 uppercase">Kalan Açık Alacak</div>
                    <div className="text-sm font-bold text-amber-700">
                      {formatCurrency(previewData.financial_impact.combined_receivables)}
                    </div>
                  </div>
                  <div className="bg-white p-2 rounded border border-slate-200">
                    <div className="text-[10px] text-slate-500 uppercase">Kullanılabilir Avans</div>
                    <div className="text-sm font-bold text-indigo-700">
                      {formatCurrency(previewData.financial_impact.combined_credit_balance)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Conflict Fields Resolver */}
              {previewData.conflict_fields?.length > 0 && (
                <div className="space-y-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                  <div className="font-semibold text-slate-800 flex items-center gap-1.5">
                    <FileText className="w-4 h-4 text-slate-500" />
                    Farklılık Gösteren Bilgiler (Seçim Yapınız):
                  </div>
                  <div className="space-y-3">
                    {previewData.conflict_fields.map((c) => (
                      <div key={c.field} className="bg-white p-2.5 rounded border border-slate-200 space-y-1.5">
                        <span className="font-medium text-slate-900">{c.label}:</span>
                        <RadioGroup
                          value={fieldOverrides[c.field] || "target"}
                          onValueChange={(val) =>
                            setFieldOverrides((prev) => ({ ...prev, [c.field]: val }))
                          }
                          className="flex flex-col sm:flex-row gap-3 pt-1"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="target" id={`${c.field}-tgt`} />
                            <Label htmlFor={`${c.field}-tgt`} className="text-xs cursor-pointer">
                              Hedefi Koru: <strong className="text-slate-800">{c.target_value}</strong>
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="source" id={`${c.field}-src`} />
                            <Label htmlFor={`${c.field}-src`} className="text-xs cursor-pointer">
                              Kaynağı Aktar: <strong className="text-slate-800">{c.source_value}</strong>
                            </Label>
                          </div>
                        </RadioGroup>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Affected Records Preview */}
              <div className="space-y-2">
                <div className="font-semibold text-slate-800">
                  Devredilecek İşler ({previewData.affected_jobs?.length || 0} Adet):
                </div>
                {previewData.affected_jobs?.length === 0 ? (
                  <div className="text-xs text-slate-500 italic p-2 bg-slate-50 rounded">
                    Kaynak müşteriye ait devredilecek kayıtlı iş bulunmuyor.
                  </div>
                ) : (
                  <div className="border border-slate-200 rounded max-h-40 overflow-y-auto">
                    <Table>
                      <TableHeader className="bg-slate-50">
                        <TableRow className="text-[11px]">
                          <TableHead>İş No</TableHead>
                          <TableHead>Proje Adı</TableHead>
                          <TableHead className="text-right">Satış Tutarı</TableHead>
                          <TableHead className="text-right">Tahsilat</TableHead>
                          <TableHead className="text-right">Kalan</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {previewData.affected_jobs.map((j) => (
                          <TableRow key={j.id} className="text-[11px]">
                            <TableCell className="font-mono">{j.job_number}</TableCell>
                            <TableCell>{j.project_name || "—"}</TableCell>
                            <TableCell className="text-right">{formatCurrency(j.sales_amount)}</TableCell>
                            <TableCell className="text-right text-green-700">{formatCurrency(j.total_collections)}</TableCell>
                            <TableCell className="text-right font-medium text-amber-700">{formatCurrency(j.balance)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>

              {/* Reason Input */}
              <div className="space-y-1.5">
                <Label htmlFor="merge-reason" className="text-xs font-medium text-slate-700">
                  Birleştirme Gerekçesi (İsteğe Bağlı)
                </Label>
                <Input
                  id="merge-reason"
                  placeholder="Örn: Aynı firma için açılmış mükerrer kartların birleştirilmesi"
                  value={mergeReason}
                  onChange={(e) => setMergeReason(e.target.value)}
                  className="text-xs"
                />
              </div>

              {/* Confirmation Checkbox */}
              <div className="p-3 bg-red-50/50 border border-red-200 rounded-lg flex items-start space-x-2">
                <Checkbox
                  id="confirm-merge"
                  checked={isConfirmed}
                  onCheckedChange={setIsConfirmed}
                  className="mt-0.5"
                />
                <Label htmlFor="confirm-merge" className="text-xs text-red-900 cursor-pointer leading-relaxed">
                  <strong>İşlem Onayı:</strong> Kaynak müşterinin pasife alınacağını, tüm işlerinin ve finansal hareketlerinin kalıcı olarak hedef müşteriye devredileceğini ve bu işlemin geri alınamayacağını onaylıyorum.
                </Label>
              </div>
            </div>
          ) : null}

          <DialogFooter className="pt-4 border-t border-slate-100 flex items-center justify-between sm:justify-between">
            <Button variant="outline" size="sm" onClick={() => setIsModalOpen(false)} disabled={isSubmitting}>
              Vazgeç
            </Button>
            <Button
              size="sm"
              onClick={handleExecuteMerge}
              disabled={isSubmitting || !isConfirmed || !previewData?.can_merge}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                  Birleştiriliyor...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                  Birleştirmeyi Onayla ve Uygula
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
